import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logAnalyticsEvent } from '../analytics/nativeAnalytics';

interface Props {
  children: ReactNode;
  /** Rendered when a descendant throws. `reset` reloads the page for a clean remount. */
  fallback: (reset: () => void) => ReactNode;
  /** Analytics label for the crash. Only the error NAME is sent — never the message. */
  analyticsEvent?: string;
  /**
   * Changing this CLEARS a caught error, so navigating away from a crashed view doesn't leave the
   * fallback outliving the route that threw.
   *
   * Deliberately a prop rather than a `key` on the boundary (David, Sep 2026). A `key` clears the
   * error by remounting the whole subtree — including on every navigation that never threw, which
   * silently reset the `useState` of every view underneath it. That is what made the hiring
   * timeline's filters vanish on a department-tab switch although the state was correctly shared;
   * the same loss applied to every URL-routed tab in the app. Resetting here instead is the same
   * behaviour where it matters (the children were unmounted while the fallback showed, so clearing
   * the flag mounts them fresh regardless) and a no-op where it doesn't.
   */
  resetKey?: string;
}

interface State {
  hasError: boolean;
  resetKey?: string;
}

/**
 * A React error boundary — the app had none, so any render-time throw tore down
 * the whole tree and left a blank white page. That is especially harmful on the
 * PUBLIC candidate booking route: a browser auto-translate mutation (the classic
 * `removeChild` crash) or any edge case would strand the candidate with no
 * message. This catches the throw and shows a recoverable fallback instead.
 *
 * Public-page safety: the crash is reported with the error NAME only (e.g.
 * "NotFoundError"); the message and component stack — which could carry candidate
 * data — never leave the device except to the console.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): Pick<State, 'hasError'> {
    return { hasError: true };
  }

  static getDerivedStateFromProps(props: Props, state: State): State | null {
    if (props.resetKey === state.resetKey) return null;
    return { hasError: false, resetKey: props.resetKey };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    void logAnalyticsEvent(this.props.analyticsEvent ?? 'error_boundary_caught', {
      error_name: error?.name || 'Error',
    });
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback(() => window.location.reload());
    }
    return this.props.children;
  }
}
